import isNumber from "lodash-es/isNumber";
import { Reducer } from "redux";

import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import {
    COMMENTS_FETCH_FAILURE,
    COMMENTS_FETCH_REQUEST,
    COMMENTS_FETCH_SUCCESS,
    COMMENT_CREATE_SUCCESS,
    COMMENT_DELETE_SUCCESS,
    CommentCreateSuccessAction,
    CommentDeleteSuccessAction,
    CommentsFetchFailureAction,
    CommentsFetchRequestAction,
    CommentsFetchSuccessAction,
} from "@/mk2/apps/blogs/containers/Comments/Comments.actions";
import {
    GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS,
    GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE,
    GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST,
    GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS,
    GroupSharedDeleteGroupPostApiSuccessAction,
    GroupSharedSubscribeGroupApiFailureAction,
    GroupSharedSubscribeGroupApiRequestAction,
    GroupSharedSubscribeGroupApiSuccessAction,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.actions";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

import {
    GROUP_DELETE_GROUP_API_SUCCESS,
    GROUP_FEED_FETCH_FAILURE,
    GROUP_FEED_FETCH_REQUEST,
    GROUP_FEED_FETCH_SUCCESS,
    GROUP_GUIDE_COMPLETE_FAILURE,
    GROUP_GUIDE_COMPLETE_REQUEST,
    GROUP_GUIDE_COMPLETE_SUCCESS,
    GROUP_POST_CREATE_SUCCESS,
    GroupDeleteGroupApiSuccessAction,
    GroupFeedFetchFailureAction,
    GroupFeedFetchRequestAction,
    GroupFeedFetchSuccessAction,
    GroupGuideCompleteFailureAction,
    GroupGuideCompleteRequestAction,
    GroupGuideCompleteSuccessAction,
    GroupPostCreateSuccessAction,
} from "./GroupFeed.actions";

export interface GroupFeedPageState {
    postIds: number[];

    eshopProductsHeaderIds: number[];
    eshopProductsIds: number[];
    eshopProductsFooterIds: number[];

    promoReviewArticlesHeaderIds: number[];
    promoReviewArticlesMiddleIds: number[];
    promoReviewArticlesFooterIds: number[];

    errorMessage: string;
    loadingState: LoadingState;
}

export const initialGroupFeedPageState: GroupFeedPageState = {
    postIds: null,

    eshopProductsHeaderIds: null,
    eshopProductsIds: null,
    eshopProductsFooterIds: null,

    promoReviewArticlesHeaderIds: null,
    promoReviewArticlesMiddleIds: null,
    promoReviewArticlesFooterIds: null,

    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface GroupFeedVirtualPageState {
    errorMessage: string;
    loadingState: LoadingState;
}

export const initialGroupFeedVirtualPageState: GroupFeedVirtualPageState = {
    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface PostsState {
    count: number;
    pages: { [pageNum: number]: GroupFeedPageState };
    virtualPages: { [pageName: string]: GroupFeedVirtualPageState }; // pageName = 'first'|'last'...
    numberOfPages: number;
}

export const initialPostsState: PostsState = {
    count: 0,
    pages: {},
    virtualPages: {},
    numberOfPages: null,
};

type PostsReducerAction =
    | GroupFeedFetchRequestAction
    | GroupFeedFetchSuccessAction
    | GroupFeedFetchFailureAction
    | GroupPostCreateSuccessAction
    | GroupSharedDeleteGroupPostApiSuccessAction;

const postsReducer: Reducer<PostsState> = (
    state = initialPostsState,
    action: PostsReducerAction,
) => {
    switch (action.type) {
        case GROUP_FEED_FETCH_REQUEST:
        case GROUP_FEED_FETCH_FAILURE:
            if (isNumber(action.pageRef)) {
                return tupdate(state, {
                    pages: tupdate(state.pages, {
                        [action.pageRef]: tupdate(
                            state.pages[action.pageRef] || initialGroupFeedPageState,
                            {
                                loadingState: loadingState(undefined, action),
                                errorMessage: errorMessage(undefined, action),
                            },
                        ),
                    }),
                });
            } else {
                return tupdate(state, {
                    virtualPages: tupdate(state.virtualPages, {
                        [action.pageRef]: tupdate(
                            state.pages[action.pageRef] || initialGroupFeedVirtualPageState,
                            {
                                loadingState: loadingState(undefined, action),
                                errorMessage: errorMessage(undefined, action),
                            },
                        ),
                    }),
                });
            }

        case GROUP_FEED_FETCH_SUCCESS:
            if (!isNumber(action.page)) {
                throw new Error("Page must be a number");
            }

            return tupdate(state, {
                count: action.count,
                numberOfPages: action.numberOfPages,
                // update state of page under both - virtual name and real index
                virtualPages: isNumber(action.pageRef)
                    ? state.virtualPages
                    : tupdate(state.virtualPages, {
                          [action.pageRef]: tupdate(
                              state.virtualPages[action.pageRef] ||
                                  initialGroupFeedVirtualPageState,
                              {
                                  loadingState: loadingState(undefined, action),
                                  errorMessage: errorMessage(undefined, action),
                              },
                          ),
                      }),
                pages: tupdate(state.pages, {
                    [action.page]: tupdate(state.pages[action.page] || initialGroupFeedPageState, {
                        postIds: action.response.result.posts,
                        eshopProductsHeaderIds: action.response.result.eshopProductsHeader,
                        eshopProductsIds: action.response.result.eshopProducts,
                        eshopProductsFooterIds: action.response.result.eshopProductsFooter,
                        promoReviewArticlesHeaderIds:
                            action.response.result.promoReviewArticlesHeader,
                        promoReviewArticlesMiddleIds:
                            action.response.result.promoReviewArticlesMiddle,
                        promoReviewArticlesFooterIds:
                            action.response.result.promoReviewArticlesFooter,
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
            });

        case GROUP_POST_CREATE_SUCCESS:
            return tupdate(state, {
                pages: tupdate(state.pages, {
                    [1]: tupdate(state.pages[1] || initialGroupFeedPageState, {
                        postIds: [
                            action.response.result.post,
                            ...(state.pages[1] ? state.pages[1].postIds || [] : []),
                        ],
                    }),
                }),
            });

        default:
            return state;
    }
};

interface GroupFeedByQueryState {
    [orderingAndQuery: string]: PostsState;
}

export const orderingAndQueryKey = (orderBy, query): string => `${orderBy}:${query || ""}`;

const searchKey = (action) => orderingAndQueryKey(action.orderBy, action.query);

export interface GroupFeedState {
    commentsLoadingState: LoadingState;
    errorMessage: string;
    hasComplaints: boolean;
    hashtags: BoxedHashtags;
    isBlocked: boolean;
    isSubscribed: boolean;
    isSubscribing: boolean;
    neighbourGroups: number[];
    moderators: number[];
    searches: GroupFeedByQueryState;
    activeBrandDaysCampaignId: number;
    activeBrandDaysPostsIds: number[];
    countyId: number;
    productsCount: number;
    userComment: boolean;
    isClosed: boolean;
    userPosts: boolean;
    commentCount: number;
    lastCreatedComments: number[];
    lastDeletedComments: number[];
}

export const initialGroupFeedState: GroupFeedState = {
    commentsLoadingState: LoadingState.INIT,
    errorMessage: null,
    hasComplaints: false,
    hashtags: {
        newest: [],
        mostUsed: [],
    },
    isBlocked: false,
    isSubscribed: false,
    isSubscribing: false,
    neighbourGroups: [],
    moderators: [],
    searches: {},
    activeBrandDaysCampaignId: null,
    activeBrandDaysPostsIds: [],
    countyId: null,
    productsCount: null,
    userComment: false,
    isClosed: false,
    userPosts: false,
    commentCount: null,
    lastCreatedComments: [],
    lastDeletedComments: [],
};

declare type GroupFeedReducerAction =
    | CommentsFetchRequestAction
    | CommentsFetchSuccessAction
    | CommentsFetchFailureAction
    | GroupGuideCompleteFailureAction
    | GroupGuideCompleteRequestAction
    | GroupGuideCompleteSuccessAction
    | GroupFeedFetchRequestAction
    | GroupFeedFetchSuccessAction
    | GroupFeedFetchFailureAction
    | GroupSharedSubscribeGroupApiFailureAction
    | GroupSharedSubscribeGroupApiRequestAction
    | GroupSharedSubscribeGroupApiSuccessAction
    | GroupDeleteGroupApiSuccessAction
    | GroupPostCreateSuccessAction
    | GroupSharedDeleteGroupPostApiSuccessAction
    | CommentDeleteSuccessAction
    | CommentCreateSuccessAction;

const groupFeedReducer: Reducer<GroupFeedState> = (
    state = initialGroupFeedState,
    action: GroupFeedReducerAction,
) => {
    switch (action.type) {
        case COMMENTS_FETCH_REQUEST:
        case COMMENTS_FETCH_SUCCESS:
        case COMMENTS_FETCH_FAILURE:
            return tupdate(state, {
                commentsLoadingState: loadingState(undefined, action),
                lastCreatedComments: state.lastCreatedComments,
                lastDeletedComments: state.lastDeletedComments,
            });

        case COMMENT_CREATE_SUCCESS:
            if (action.commentParent.model === "message") {
                return tupdate(state, {
                    lastCreatedComments: [...state.lastCreatedComments, action.commentParent.id],
                });
            } else return state;

        case COMMENT_DELETE_SUCCESS:
            if (action.commentParent.model === "message") {
                return tupdate(state, {
                    lastDeletedComments: [...state.lastDeletedComments, action.commentParent.id],
                });
            } else return state;

        case GROUP_GUIDE_COMPLETE_REQUEST:
        case GROUP_GUIDE_COMPLETE_FAILURE:
            return tupdate(state, {});
        case GROUP_GUIDE_COMPLETE_SUCCESS:
            return tupdate(state, {
                isClosed: action.isClosed,
            });

        case GROUP_FEED_FETCH_REQUEST:
        case GROUP_FEED_FETCH_FAILURE:
            return tupdate(state, {
                searches: tupdate(state.searches, {
                    [searchKey(action)]: postsReducer(state.searches[searchKey(action)], action),
                }),
            });

        case GROUP_FEED_FETCH_SUCCESS:
            return tupdate(state, {
                searches: tupdate(state.searches, {
                    [searchKey(action)]: postsReducer(state.searches[searchKey(action)], action),
                }),
                hasComplaints: action.hasComplaints,
                hashtags: action.hashtags,
                isBlocked: action.isBlocked,
                isSubscribed: action.isSubscribed,
                neighbourGroups: action.response.result.neighbourGroups,
                moderators: action.response.result.moderators,
                activeBrandDaysCampaignId: action.response.result.activeBrandDays.campaign,
                activeBrandDaysPostsIds: action.response.result.activeBrandDays.posts,
                countyId: action.countyId,
                productsCount: action.productsCount,
                userComment: action.userComment,
                userPosts: action.userPosts,
                isClosed: action.isClosed,
                commentCount: action.commentCount,
            });

        case GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE:
        case GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST:
            return tupdate(state, {
                isSubscribing: loadingState(undefined, action) === LoadingState.LOADING,
            });

        case GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS:
            return tupdate(state, {
                isSubscribed: action.isSubscribed,
                isSubscribing: false,
            });

        case GROUP_POST_CREATE_SUCCESS:
            return tupdate(state, {
                searches: tupdate(state.searches, {
                    [orderingAndQueryKey("posts", null)]: postsReducer(
                        state.searches[orderingAndQueryKey("posts", null)],
                        action,
                    ),
                }),
            });

        case GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS:
            return tupdate(state, {
                searches: Object.keys(state.searches).reduce((acc, key) => {
                    acc[key] = postsReducer(state.searches[key], action);
                    return acc;
                }, {}),
            });

        case GROUP_DELETE_GROUP_API_SUCCESS:
            return state;

        default:
            return state;
    }
};

export interface State {
    [id: number]: GroupFeedState;
}
export const initialState: State = {};

declare type GroupFeedsReducerAction =
    | CommentsFetchRequestAction
    | CommentsFetchSuccessAction
    | CommentsFetchFailureAction
    | GroupGuideCompleteSuccessAction
    | GroupFeedFetchRequestAction
    | GroupFeedFetchSuccessAction
    | GroupFeedFetchFailureAction
    | GroupSharedSubscribeGroupApiFailureAction
    | GroupSharedSubscribeGroupApiRequestAction
    | GroupSharedSubscribeGroupApiSuccessAction
    | GroupPostCreateSuccessAction
    | GroupSharedDeleteGroupPostApiSuccessAction
    | CommentCreateSuccessAction
    | CommentDeleteSuccessAction;

const groupFeedsReducer: (
    state: State,
    action: GroupFeedsReducerAction,
) => { [p: number]: GroupFeedState } = (
    state: State = initialState,
    action: GroupFeedsReducerAction,
) => {
    switch (action.type) {
        case GROUP_FEED_FETCH_SUCCESS:
        case GROUP_FEED_FETCH_REQUEST:
        case GROUP_FEED_FETCH_FAILURE:
        case GROUP_SHARED_SUBSCRIBE_GROUP_API_FAILURE:
        case GROUP_SHARED_SUBSCRIBE_GROUP_API_REQUEST:
        case GROUP_SHARED_SUBSCRIBE_GROUP_API_SUCCESS:
        case GROUP_POST_CREATE_SUCCESS:
            return tupdate(state, {
                [action.groupId]: groupFeedReducer(state[action.groupId], action),
            });

        case COMMENTS_FETCH_REQUEST:
        case COMMENTS_FETCH_SUCCESS:
        case COMMENT_CREATE_SUCCESS:
        case COMMENT_DELETE_SUCCESS:
        case COMMENTS_FETCH_FAILURE:
        case GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS:
            return Object.keys(state).reduce((acc, groupId) => {
                acc[groupId] = groupFeedReducer(state[groupId], action);
                return acc;
            }, {});
        default:
            return state;
    }
};

export default groupFeedsReducer;
